// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ContentCopyRounded,
  Edit as EditIcon,
  FactCheckOutlined as FactCheckIcon,
  Inventory as InventoryIcon,
  LocalShipping as LocalShippingIcon,
} from '@/constants/hardfin-standard-icons';
import { FulfillmentSyncSignal } from '@/fulfillment/components/fulfillment-sync-signal';
import { type FulfillmentOrderStatus } from '@/fulfillment/constants/fulfillment-order-status';
import { Button, IconButton, Stack, Tooltip } from '@mui/material';
import { DateTime } from 'luxon';

interface HeaderActionsProps {
  status: FulfillmentOrderStatus;
  originLocationIsThirdPartyEnabled: boolean;
  isThirdPartyFulfilled: boolean;
  isClosed: boolean;
  hasZeroQuantityItem: boolean;
  fulfillmentKey: string;
  fulfillmentNumber: string;
  lastSyncedAt: DateTime | null;
  lastThirdPartyUpdateAt: DateTime | null;
  hasSyncError: boolean;
  syncErrorMessage: string;
  // Open quarantined serials on this order, summed across its line items
  quarantineCount: number;
  onEdit: () => void;
  onCancel: () => void;
  onCloseOrder: () => void;
  onFulfill: () => void;
  onSendTo3PL: () => void;
  onClone: () => void;
}

const ZERO_QUANTITY_TOOLTIP =
  'Edit the order and remove or set a positive quantity on every line item before continuing';
const FULFILL_TOOLTIP =
  'Record shipment activity for this order (use when more items should still be opened, shipped, or delivered)';
const CLOSE_ORDER_TOOLTIP =
  'Manually close this partial shipped or delivered order short when no more shipments are expected';
const CANCEL_ORDER_TOOLTIP =
  'Cancel this draft order because it should not be fulfilled. Orders with opened shipments must have those shipments cancelled first';

/**
 * Component that displays action buttons in the fulfillment order header.
 * Shows different buttons based on the current status and 3PL configuration.
 */
export function FulfillmentHeaderActions(props: HeaderActionsProps) {
  const {
    status,
    originLocationIsThirdPartyEnabled,
    isThirdPartyFulfilled,
    isClosed,
    hasZeroQuantityItem,
    fulfillmentKey,
    fulfillmentNumber,
    lastSyncedAt,
    lastThirdPartyUpdateAt,
    hasSyncError,
    syncErrorMessage,
    quarantineCount,
    onEdit,
    onCloseOrder,
    onFulfill,
    onSendTo3PL,
    onClone,
  } = props;

  // Determine button visibility and state based on status and 3PL
  const showEdit = !isClosed && status === 'DRAFT';

  // For DRAFT: show "Send to 3PL" if origin location is 3PL enabled
  const showSendTo3PL =
    !isClosed && originLocationIsThirdPartyEnabled && status === 'DRAFT';

  // The 3PL sync signal shows for any 3PL-bound order regardless of status, so
  // ops can read sync timing and re-check a stuck or already-closed order.
  const showSyncSignal = isThirdPartyFulfilled;

  // A shipment can join a non-3PL order until it is closed, cancelled, or fully
  // delivered. A draft at a 3PL-enabled origin goes through "Send to 3PL" instead.
  const isFullyDelivered =
    status === 'DELIVERED_FULL' || status === 'DELIVERED_OVERAGE';
  const showFulfill =
    !isThirdPartyFulfilled &&
    !isClosed &&
    status !== 'CANCELLED' &&
    !isFullyDelivered &&
    !(status === 'DRAFT' && originLocationIsThirdPartyEnabled);

  // Cancel voids the draft fulfillment obligation before shipment work starts.
  // Once shipments exist, users cancel those shipments first so lifecycle sync
  // can move the FO back to DRAFT before cancellation.
  const showCancel = !isThirdPartyFulfilled && !isClosed && status === 'DRAFT';

  // Close keeps the partial fulfillment facts intact, but stops treating this
  // non-3PL partial order as active for reporting once no more shipments are
  // expected.
  const showCloseOrder =
    !isThirdPartyFulfilled &&
    !isClosed &&
    (status === 'SHIPPED_PARTIAL' || status === 'DELIVERED_PARTIAL');

  // Only block draft-advancing actions while still in DRAFT — once the order
  // has moved past DRAFT the user can no longer Edit, so blocking would leave
  // them with no path forward.
  const blockDraftAdvance = status === 'DRAFT' && hasZeroQuantityItem;

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {/* Edit button */}
      {showEdit && (
        <Button
          variant="outlined"
          size="medium"
          startIcon={<EditIcon />}
          onClick={onEdit}
        >
          Edit
        </Button>
      )}

      {/* 3PL sync-status signal (replaces the old indicator + resync button) */}
      {showSyncSignal && (
        <FulfillmentSyncSignal
          fulfillmentKey={fulfillmentKey}
          fulfillmentNumber={fulfillmentNumber}
          status={status}
          isClosed={isClosed}
          lastSyncedAt={lastSyncedAt}
          lastThirdPartyUpdateAt={lastThirdPartyUpdateAt}
          hasSyncError={hasSyncError}
          syncErrorMessage={syncErrorMessage}
          quarantineCount={quarantineCount}
        />
      )}

      {/* Send to 3PL button */}
      {showSendTo3PL && (
        <Tooltip arrow title={blockDraftAdvance ? ZERO_QUANTITY_TOOLTIP : ''}>
          <span>
            <Button
              variant="contained"
              size="medium"
              startIcon={<LocalShippingIcon />}
              onClick={onSendTo3PL}
              disabled={blockDraftAdvance}
            >
              Send to 3PL
            </Button>
          </span>
        </Tooltip>
      )}

      {/* Fulfill button */}
      {showFulfill && (
        <Tooltip
          arrow
          title={blockDraftAdvance ? ZERO_QUANTITY_TOOLTIP : FULFILL_TOOLTIP}
        >
          <span>
            <Button
              variant="contained"
              size="medium"
              startIcon={<InventoryIcon />}
              onClick={onFulfill}
              disabled={blockDraftAdvance}
            >
              Fulfill
            </Button>
          </span>
        </Tooltip>
      )}

      {showCloseOrder && (
        <Tooltip arrow title={CLOSE_ORDER_TOOLTIP}>
          <span>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<FactCheckIcon />}
              onClick={onCloseOrder}
            >
              Close order
            </Button>
          </span>
        </Tooltip>
      )}

      {/* Cancel order button */}
      {showCancel && (
        <Tooltip arrow title={CANCEL_ORDER_TOOLTIP}>
          <span>
            <Button
              variant="contained"
              size="medium"
              color="error"
              onClick={props.onCancel}
            >
              Cancel order
            </Button>
          </span>
        </Tooltip>
      )}

      {/* Clone button */}
      <Tooltip arrow title="Clone this fulfillment order">
        <IconButton size="small" onClick={onClone}>
          <ContentCopyRounded fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
